import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone, User, ShoppingCart, ChevronDown, Monitor, Globe, Wrench, Calendar, Printer } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/hooks/useAuth";

const navLinks = [
  { to: "/", label: "Accueil" },
  { to: "/boutique", label: "Boutique" },
  { to: "/pc-sur-mesure", label: "PC Sur Mesure" },
  { to: "/creation-web", label: "Création Web" },
  { to: "/rdv", label: "Rendez-vous" },
  { to: "/blog", label: "Blog" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

const serviceCards = [
  { to: "/pc-sur-mesure", title: "PC Sur Mesure", text: "PC fixe dès 580€", icon: Monitor, tone: "bg-tile-sky" },
  { to: "/creation-web", title: "Création Web", text: "Vitrine dès 590€", icon: Globe, tone: "bg-tile-lilac" },
  { to: "/assistance-bureautique", title: "Bureautique", text: "Word, e-mail, imprimante", icon: Printer, tone: "bg-tile-lime" },
  { to: "/contact", title: "Dépannage", text: "Diagnostic 75€", icon: Wrench, tone: "bg-tile-sun" },
  { to: "/rdv", title: "Rendez-vous", text: "24h/24 · 7j/7", icon: Calendar, tone: "bg-tile-sky" },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { user, isAdmin } = useAuth();
  const { itemCount } = useCart();
  const account = user ? (isAdmin ? "/admin" : "/dashboard") : "/auth";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  const desktopLinks = navLinks.filter((l) => !["/pc-sur-mesure", "/creation-web", "/rdv"].includes(l.to));

  return (
    <nav className="fixed top-0 inset-x-0 z-50 safe-top px-3 md:px-6 pt-2 md:pt-3">
      <div
        className={`mx-auto flex items-center justify-between rounded-full border transition-all duration-300 ${
          scrolled
            ? "max-w-5xl h-14 bg-card/90 border-border shadow-card-premium px-4 backdrop-blur-md"
            : "max-w-7xl h-16 bg-transparent border-transparent px-2 md:px-4"
        }`}
      >
        <Link to="/" className="flex items-center min-w-0" aria-label="SUDWEB-TECH, accueil">
          <span className="font-display font-extrabold text-lg md:text-xl tracking-tighter text-foreground truncate">
            SUDWEB<span className="text-primary">-TECH</span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-0.5">
          <div className="relative" onMouseEnter={() => setServicesOpen(true)} onMouseLeave={() => setServicesOpen(false)}>
            <button
              onClick={() => setServicesOpen((v) => !v)}
              aria-expanded={servicesOpen}
              className="flex items-center gap-1 px-3.5 py-2 rounded-full text-sm font-medium text-foreground/80 hover:text-foreground hover:bg-secondary transition-colors"
            >
              Services <ChevronDown size={14} className={`transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {servicesOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.98 }}
                  transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute left-1/2 -translate-x-1/2 top-full pt-3"
                >
                  <div className="grid grid-cols-2 gap-2 p-2 w-[460px] rounded-3xl bg-card border border-border shadow-elevated">
                    {serviceCards.map((c) => (
                      <Link key={c.to + c.title} to={c.to} className={`${c.tone} rounded-2xl p-4 text-tile-ink hover:scale-[1.02] transition-transform`}>
                        <c.icon size={22} />
                        <div className="mt-6 font-display font-extrabold text-lg leading-none">{c.title}</div>
                        <div className="text-sm opacity-75 mt-1">{c.text}</div>
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          {desktopLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`px-3.5 py-2 rounded-full text-sm font-medium transition-colors ${
                location.pathname === link.to ? "text-foreground bg-secondary" : "text-foreground/70 hover:text-foreground hover:bg-secondary"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-1.5">
          <Link to="/panier" className="relative p-2.5 rounded-full hover:bg-secondary" aria-label={`Panier${itemCount > 0 ? ` (${itemCount})` : " (vide)"}`}>
            <ShoppingCart size={18} />
            {itemCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 h-5 min-w-5 px-1 rounded-full bg-primary text-primary-foreground text-[11px] font-bold flex items-center justify-center">{itemCount}</span>
            )}
          </Link>
          <Link to={account} className="p-2.5 rounded-full hover:bg-secondary" aria-label={user ? "Mon espace" : "Connexion"}>
            <User size={18} />
          </Link>
          <motion.a
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
            href="tel:+33669260492"
            className="ml-1 inline-flex items-center gap-2 rounded-full bg-foreground text-background px-5 h-10 text-sm font-semibold"
          >
            <Phone size={14} /> 06 69 26 04 92
          </motion.a>
        </div>

        <button
          onClick={() => setIsOpen(!isOpen)}
          className="md:hidden text-foreground h-11 w-11 flex items-center justify-center rounded-full bg-card border border-border"
          aria-label={isOpen ? "Fermer le menu" : "Ouvrir le menu"}
          aria-expanded={isOpen}
        >
          {isOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, clipPath: "circle(0% at 95% 4%)" }}
            animate={{ opacity: 1, clipPath: "circle(150% at 95% 4%)" }}
            exit={{ opacity: 0, clipPath: "circle(0% at 95% 4%)" }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden fixed inset-0 -z-10 bg-background pt-24 pb-28 px-6 overflow-y-auto"
          >
            <ul className="flex flex-col">
              {navLinks.map((link, i) => (
                <motion.li
                  key={link.to}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.04, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                >
                  <Link
                    to={link.to}
                    className={`block py-2.5 font-display font-extrabold text-4xl tracking-tighter ${location.pathname === link.to ? "text-primary" : "text-foreground"}`}
                  >
                    {link.label}
                  </Link>
                </motion.li>
              ))}
            </ul>
            <div className="mt-8 flex flex-col gap-3">
              <a href="tel:+33669260492" className="flex items-center justify-center gap-2 h-14 rounded-full bg-primary text-primary-foreground font-semibold text-base">
                <Phone size={18} /> Appeler le 06 69 26 04 92
              </a>
              <Link to={account} className="flex items-center justify-center gap-2 h-14 rounded-full border border-border bg-card font-semibold">
                <User size={18} /> {user ? "Mon Espace" : "Connexion"}
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
